import * as XLSX from "xlsx";

const IST = "Asia/Kolkata";

// Ceiling on a single "download all" / totals fetch. Named so the limit is
// visible instead of a bare 9999 scattered through the views — callers compare
// it against the server's reported total and warn rather than truncate silently.
export const EXPORT_LIMIT = 9999;

// ─── Shared formatters ────────────────────────────────────────────────────────

// Explicit timeZone: the API's dates already pass through pg's local-timezone
// Date conversion, so without it a phone not set to IST shows dates off by one.
export const fmtDate = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString("en-IN", {
        timeZone: IST,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

// YYYY-MM-DD in IST. toISOString() is UTC, so between 00:00 and 05:30 IST it
// returns the previous day — which silently shifted the default date ranges.
export const toISODate = (d = new Date()) =>
  d.toLocaleDateString("en-CA", { timeZone: IST });

// The dairy bills in fixed brackets — 1–10, 11–20, 21–end of month — so the member
// must default to the same period, not to "the last 10 calendar days" which would
// straddle two bills. Mirrors getDateRange() in the server's milk-data.service.js.
export const currentTenDayRange = (d = new Date()) => {
  const [y, m, day] = toISODate(d).split("-").map(Number);
  const pad = (n) => String(n).padStart(2, "0");
  const start = day <= 10 ? 1 : day <= 20 ? 11 : 21;
  const end = day <= 10 ? 10 : day <= 20 ? 20 : new Date(y, m, 0).getDate();
  return { from: `${y}-${pad(m)}-${pad(start)}`, to: `${y}-${pad(m)}-${pad(end)}` };
};

// Filename fragment for a range that may be unset (advance/supply start unfiltered).
export const rangeTag = (from, to) => (from && to ? `${from}-${to}` : "sarva");

// Display only — keeps trailing zeros for the on-screen summary cards.
export const fmtNum = (v) => Number(v || 0).toFixed(2);

// Excel cells — a real number, so the column can be summed, sorted and
// right-aligned. Blank (not "-") when absent, which would force the whole
// column to text.
export const toNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : "";
};

// ─── Column-mapping helpers ───────────────────────────────────────────────────

export const milkEntryToRow = (r) => ({
  "दिनांक": fmtDate(r.date),
  "वेळ": r.mr_milking_shift || "",
  "लि.": toNum(r.qty),
  "फॅट": toNum(r.fat),
  "SNF": toNum(r.snf),
  "दर (₹)": toNum(r.rate),
  "रक्कम (₹)": toNum(r.total_rate),
});

export const billToRow = (b) => ({
  "पासून": fmtDate(b.from_date),
  "पर्यंत": fmtDate(b.to_date),
  "दूध (लि.)": toNum(b.totalmilk),
  "एकूण दर (₹)": toNum(b.totalrate),
  "कपात (₹)": toNum(b.cutting),
  "देय रक्कम (₹)": toNum(b.grandtotal),
});

export const advanceToRow = (r) => ({
  "तारीख": fmtDate(r.date),
  "जमा (रु.)": toNum(r.credit),
  "कपात (रु.)": toNum(r.debit),
});

export const supplyToRow = (r) => ({
  "तारीख": fmtDate(r.date),
  "प्रकार": r.type || "",
  "पिशवी": toNum(r.bags),
  "दर": toNum(r.rate),
  "रक्कम": toNum(r.total_rate),
  "कपात": toNum(r.debit),
});

// ─── Member info header (from localStorage) ───────────────────────────────────

export const buildMemberInfoAoa = ({ from, to } = {}) => {
  const rows = [
    ["डेअरीचे नाव", localStorage.getItem("dairy_name") || "-"],
    ["सभासदाचे नाव", localStorage.getItem("user_name") || "-"],
    ["डेअरी आयडी", localStorage.getItem("dairy_id") || "-"],
  ];
  if (from && to) rows.push(["कालावधी", `${fmtDate(from)} — ${fmtDate(to)}`]);
  rows.push([]); // blank separator
  return rows;
};

// ─── Sheet builder ────────────────────────────────────────────────────────────

/**
 * Layout:
 *   member info rows   label | value (merged to last column)
 *   blank separator    (merged full width)
 *   data table         column headers + rows
 *   blank gap
 *   summary rows       label | value (merged), or a single cell merged full width
 *
 * Row positions are derived from what was actually written rather than
 * recomputed from counts, so changing the layout can't silently misplace merges.
 */
export const buildStyledSheet = ({ rows, summaryAoa = [], from, to }) => {
  const headerAoa = buildMemberInfoAoa({ from, to });
  const ws = XLSX.utils.aoa_to_sheet(headerAoa);

  const headers = Object.keys(rows[0] || {});
  const lastCol = Math.max(headers.length - 1, 1);

  const dataHeaderRow = headerAoa.length;
  XLSX.utils.sheet_add_json(ws, rows, { origin: { r: dataHeaderRow, c: 0 } });
  const lastDataRow = dataHeaderRow + rows.length;

  let summaryStart = null;
  if (summaryAoa.length) {
    summaryStart = lastDataRow + 2; // one blank row of gap
    XLSX.utils.sheet_add_aoa(ws, summaryAoa, { origin: { r: summaryStart, c: 0 } });
  }

  const merges = [];
  for (let r = 0; r < headerAoa.length - 1; r++) {
    merges.push({ s: { r, c: 1 }, e: { r, c: lastCol } });
  }
  const blankRow = headerAoa.length - 1;
  merges.push({ s: { r: blankRow, c: 0 }, e: { r: blankRow, c: lastCol } });

  if (summaryStart !== null) {
    summaryAoa.forEach((row, i) => {
      const r = summaryStart + i;
      if (row.length > 1) merges.push({ s: { r, c: 1 }, e: { r, c: lastCol } });
      else if (row.length === 1) merges.push({ s: { r, c: 0 }, e: { r, c: lastCol } });
    });
  }
  ws["!merges"] = merges;

  // Column widths — Marathi headers are long and arrive cut off otherwise.
  // Column A also carries the header/summary labels, so give it extra room.
  ws["!cols"] = headers.map((h, i) => ({
    wch: i === 0 ? Math.max(h.length + 4, 18) : Math.max(h.length + 4, 12),
  }));

  // Two decimals on every numeric cell, so amounts read as money.
  Object.keys(ws).forEach((addr) => {
    if (addr[0] === "!") return;
    if (ws[addr].t === "n") ws[addr].z = "0.00";
  });

  return ws;
};

// ─── Download ─────────────────────────────────────────────────────────────────

const downloadExcel = ({ fileName, sheetName, rows, summaryAoa, from, to }) => {
  const ws = buildStyledSheet({ rows, summaryAoa, from, to });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};

export default downloadExcel;
